import PerfectScrollbar from 'react-perfect-scrollbar';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { NavLink, useLocation } from 'react-router-dom';
import { toggleSidebar } from '../../store/themeConfigSlice';
import AnimateHeight from 'react-animate-height';
import { IRootState } from '../../store';
import { useState, useEffect, useMemo } from 'react';
import IconCaretsDown from '../Icon/IconCaretsDown';
import IconCaretDown from '../Icon/IconCaretDown';
import IconMinus from '../Icon/IconMinus';
import IconMenuDashboard from '../Icon/Menu/IconMenuDashboard';
import IconSearch from '../Icon/IconSearch';
import { menuItems } from '../../lib/constants';
import { IsAccessableComponent } from '../IsAuthenticated';

const Sidebar = () => {
  const [currentMenu, setCurrentMenu] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const themeConfig = useSelector((state: IRootState) => state.themeConfig);
  const semidark = useSelector((state: IRootState) => state.themeConfig.semidark);
  const location = useLocation();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const toggleMenu = (value: string) => {
    setCurrentMenu((oldValue) => {
      return oldValue === value ? '' : value;
    });
  };

  // Filter menu items based on search term
  const filteredMenuItems = useMemo(() => {
    if (!searchTerm) return menuItems;

    return menuItems
      .map((section) => ({
        ...section,
        submenu: section.submenu.filter(
          (item) => t(item.title).toLowerCase().includes(searchTerm.toLowerCase())
          //   ||
          //     (item.submenu &&
          //       item.submenu.some((subItem) =>
          //         t(subItem.title).toLowerCase().includes(searchTerm.toLowerCase())
          //       ))
        ),
      }))
      .filter((section) => section.submenu.length > 0);
  }, [menuItems, searchTerm, t]);

  useEffect(() => {
    const selector = document.querySelector(
      '.sidebar ul a[href="' + window.location.pathname + '"]'
    );
    if (selector) {
      selector.classList.add('active');
      const ul: any = selector.closest('ul.sub-menu');
      if (ul) {
        let ele: any = ul.closest('li.menu').querySelectorAll('.nav-link') || [];
        if (ele.length) {
          ele = ele[0];
          setTimeout(() => {
            ele.click();
          });
        }
      }
    }
  }, []);

  useEffect(() => {
    if (window.innerWidth < 1024 && themeConfig.sidebar) {
      dispatch(toggleSidebar());
    }
  }, [location, dispatch, themeConfig.sidebar]);

  return (
    <div className={semidark ? 'dark' : ''}>
      <nav
        className={`sidebar fixed min-h-screen h-full top-0 bottom-0 w-[260px] shadow-[5px_0_25px_0_rgba(94,92,154,0.1)] z-50 transition-all duration-300 ${semidark ? 'text-white-dark' : ''}`}
      >
        <div className="bg-white dark:bg-black h-full">
          <div className="flex justify-between items-center px-4 py-3">
            <NavLink to="/" className="main-logo flex items-center shrink-0">
              <img
                className="w-8 ml-[5px] flex-none"
                src="https://portfolio-image-store.s3.amazonaws.com/WEBSITE-IMAGES+/1708869509679-apple-touch-icon.png"
                alt="logo"
              />
              <span className="text-2xl ltr:ml-1.5 rtl:mr-1.5 font-semibold align-middle lg:inline dark:text-white-light">
                {t('SharePal')}
              </span>
            </NavLink>

            <button
              type="button"
              className="collapse-icon w-8 h-8 rounded-full flex items-center hover:bg-gray-500/10 dark:hover:bg-dark-light/10 dark:text-white-light transition duration-300 rtl:rotate-180"
              onClick={() => dispatch(toggleSidebar())}
            >
              <IconCaretsDown className="m-auto rotate-90" />
            </button>
          </div>

          {/* Search Bar */}
          <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
            <div className="relative">
              <input
                type="text"
                className="form-input pl-9 w-full"
                placeholder={t('Search menu...')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <IconSearch className="absolute left-3 top-2.5 text-gray-400" />
            </div>
          </div>

          <PerfectScrollbar className="h-[calc(100vh-130px)] relative">
            <ul className="relative font-semibold space-y-0.5 p-4 py-0">
              {/* Dashboard Section */}
              <li className="menu nav-item">
                <button
                  type="button"
                  className={`${currentMenu === 'dashboard' ? 'active' : ''} nav-link group w-full`}
                  onClick={() => toggleMenu('dashboard')}
                >
                  <div className="flex items-center">
                    <IconMenuDashboard className="group-hover:!text-primary shrink-0" />
                    <span className="ltr:pl-3 rtl:pr-3 text-black dark:text-[#506690] dark:group-hover:text-white-dark">
                      {t('dashboard')}
                    </span>
                  </div>

                  <div className={currentMenu !== 'dashboard' ? 'rtl:rotate-90 -rotate-90' : ''}>
                    <IconCaretDown />
                  </div>
                </button>

                <AnimateHeight duration={300} height={currentMenu === 'dashboard' ? 'auto' : 0}>
                  <ul className="sub-menu text-gray-500">
                    <li>
                      <NavLink to="/">{t('sales')}</NavLink>
                    </li>
                  </ul>
                </AnimateHeight>
              </li>

              {filteredMenuItems.map((menuItem, index) => (
                <div key={index}>
                  <h2 className="py-3 px-7 flex items-center uppercase font-extrabold bg-white-light/30 dark:bg-dark dark:bg-opacity-[0.08] -mx-4 mb-1">
                    <IconMinus className="w-4 h-5 flex-none hidden" />
                    <span>{t(menuItem.title)}</span>
                  </h2>

                  {menuItem?.submenu?.map((subMenuItem, subIndex) => (
                    <IsAccessableComponent key={subIndex} path={subMenuItem.link} type={'sidebar'}>
                      <li className={'nav-item '}>
                        <ul>
                          {/* {subMenuItem?.submenu ? (
                                                        <li className="menu nav-item">
                                                            <button
                                                                type="button"
                                                                className={`${currentMenu === subMenuItem.title ? 'active' : ''} nav-link group w-full`}
                                                                onClick={() => toggleMenu(subMenuItem.title)}
                                                            >
                                                                <div className="flex items-center">
                                                                    {subMenuItem.icon}
                                                                    <span className="ltr:pl-3 rtl:pr-3 text-black dark:text-[#506690] dark:group-hover:text-white-dark">{t(subMenuItem.title)}</span>
                                                                </div>

                                                                <div className={currentMenu !== subMenuItem.title ? 'rtl:rotate-90 -rotate-90' : ''}>
                                                                    <IconCaretDown />
                                                                </div>
                                                            </button>
                                                            <AnimateHeight duration={300} height={currentMenu === subMenuItem.title ? 'auto' : 0}>
                                                                <ul className="sub-menu text-gray-500">
                                                                    {subMenuItem?.submenu?.map((innerSubMenuItem, innerSubIndex) => (
                                                                        <li key={innerSubIndex}>
                                                                            <NavLink to={innerSubMenuItem?.link}>{t(innerSubMenuItem.title)}</NavLink>
                                                                        </li>
                                                                    ))}
                                                                </ul>
                                                            </AnimateHeight>
                                                        </li>
                                                    ) : ( */}
                          <li className="nav-item">
                            <NavLink to={subMenuItem?.link} className="group">
                              <div className="flex items-center">
                                {subMenuItem.icon}
                                <span className="ltr:pl-3 rtl:pr-3 text-black dark:text-[#506690] dark:group-hover:text-white-dark">
                                  {t(subMenuItem.title)}
                                </span>
                              </div>
                            </NavLink>
                          </li>
                          {/* )} */}
                        </ul>
                      </li>
                    </IsAccessableComponent>
                  ))}
                </div>
              ))}
            </ul>
          </PerfectScrollbar>
        </div>
      </nav>
    </div>
  );
};

export default Sidebar;

//
